import { use, useDeferredValue, useEffect, useMemo } from 'react';
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { useDispatch, useSelector } from 'react-redux';
import useKeepPreviousData from '@helsenorge/core-utils/useKeepPreviousData';
import { selectCurrentLanguage } from '../language';
import { error as logError } from '../logger';
import { getModifiedResourcesForTest } from './test-utils';
export { getModifiedResourcesForTest } from './test-utils';
export const createResourcesApi = (options) => {
    const resourcesApi = createApi({
        reducerPath: 'resourcesApi',
        baseQuery: fakeBaseQuery(),
        endpoints: builder => ({
            getResources: builder.query({
                queryFn: async ({ projectName, language }) => {
                    try {
                        const resources = await options.fetchResources(projectName, language);
                        return { data: resources };
                    }
                    catch (error) {
                        return { error: { message: error instanceof Error ? error.message : String(error) } };
                    }
                },
            }),
        }),
    });
    const useResources = (projectName) => {
        const language = useSelector(state => selectCurrentLanguage(state.language));
        // Hent resources fra bundle (f.eks. norsk bokmål)
        const rawBundled = options.getBundledResources(projectName, language);
        const bundledResources = rawBundled ? getModifiedResourcesForTest(rawBundled) : undefined;
        const fallbackResources = options.fallbackLanguage ? options.getBundledResources(projectName, options.fallbackLanguage) : undefined;
        // Hent resources asynkront hvis de ikke er i bundle (alle andre språk)
        const { data: asyncResources, isError, error, } = resourcesApi.useGetResourcesQuery({ projectName, language }, {
            skip: !!bundledResources,
            selectFromResult: ({ data, isError, error }) => ({
                data: getModifiedResourcesForTest(data),
                isError,
                error,
            }),
        });
        useEffect(() => {
            if (isError && error) {
                logError(`Klarte ikke laste resources for "${projectName}" og språk "${language}".`, error);
            }
        }, [isError, error, language, projectName]);
        const currentResources = bundledResources ?? asyncResources ?? undefined;
        // Behold tekster mens nytt språk lastes
        const previousResources = useKeepPreviousData(currentResources);
        // Håndter fallback og feil
        const finalResources = useMemo(() => {
            if (previousResources) {
                if (options.useFallbackForMissingTexts && fallbackResources) {
                    return { ...fallbackResources, ...previousResources };
                }
                return previousResources;
            }
            if (isError && fallbackResources) {
                return fallbackResources;
            }
            return null;
        }, [previousResources, fallbackResources, isError]);
        return finalResources;
    };
    const useSuspenseResources = (projectName) => {
        const dispatch = useDispatch();
        // Bruk useDeferredValue for å unngå at komponenten suspender mens vi laster nye ressurser
        const currentLanguage = useSelector(state => selectCurrentLanguage(state.language));
        const language = useDeferredValue(currentLanguage);
        // Et promise med tekstene, som enten er fra bundle eller asynkront lastet
        const resourcePromise = useMemo(() => {
            const bundled = options.getBundledResources(projectName, language);
            if (bundled) {
                return Promise.resolve(getModifiedResourcesForTest(bundled));
            }
            const promise = dispatch(resourcesApi.endpoints.getResources.initiate({ projectName, language }));
            return promise
                .unwrap()
                .then(data => getModifiedResourcesForTest(data))
                .catch(error => {
                logError(`Klarte ikke laste resources for "${projectName}" og språk "${language}".`, error);
                const fallback = options.fallbackLanguage ? options.getBundledResources(projectName, options.fallbackLanguage) : undefined;
                if (fallback) {
                    return fallback;
                }
                throw error;
            });
        }, [language, projectName, dispatch]);
        const data = use(resourcePromise);
        if (options.useFallbackForMissingTexts && options.fallbackLanguage) {
            const fallback = options.getBundledResources(projectName, options.fallbackLanguage);
            return { ...fallback, ...data };
        }
        return data;
    };
    return {
        resourcesApi,
        useResources,
        useSuspenseResources,
    };
};
